/**
 * Load a van, unload it, or move goods between two warehouses.
 *
 * From and To, then the items -- scanned or typed, in cartons or pieces --
 * with what the from-store holds shown on every line. Nothing can leave a
 * store that does not have it: unlike a sale, the company's negative-stock
 * setting does not apply here. Ctrl+Enter saves; the transfer opens ready
 * to print as a load sheet for the driver to sign.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Box, Button, IconButton, MenuItem, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import SwapIcon from '@mui/icons-material/SwapHoriz'
import { useTranslation } from 'react-i18next'

import { createTransfer, fetchInvItems, fetchStores } from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P, type InvItem } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { useUnsavedChanges } from '../store/UnsavedChanges'
import { useHotkeys } from '../hotkeys/HotkeyProvider'
import Keycap from '../components/Keycap'
import ScreenTitle from '../components/ScreenTitle'
import { sanitize } from '../vouchers/money'
import { toHijri } from '../reports/hijri'
import { today } from '../dates'
import { c } from '../theme'
import { fmtQty } from './qty'
import { factorOf } from './SaleEntry'
import { ItemPicker, StoreSelect, unitsOf } from './bits'

interface Line { key: number; item: InvItem | null; unit: string; qty: string }

let nextKey = 1
const blank = (): Line => ({ key: nextKey++, item: null, unit: '', qty: '' })

export default function TransferEntry() {
  const { t, i18n } = useTranslation()
  const nav = useNavigate()
  const qc = useQueryClient()
  const { can } = useAuth()

  const stores = useQuery({ queryKey: ['inv-stores'], queryFn: fetchStores })
  const items = useQuery({ queryKey: ['inv-items', '', '', 'active'],
                           queryFn: () => fetchInvItems({ active: true }) })
  const [date, setDate] = useState(today())
  const [from, setFrom] = useState<number | ''>('')
  const [to, setTo] = useState<number | ''>('')
  const [notes, setNotes] = useState('')
  const [lines, setLines] = useState<Line[]>([blank()])
  const [error, setError] = useState<string | null>(null)

  // The usual morning: from the warehouse to the first van.
  useEffect(() => {
    const list = (stores.data ?? []).filter((s) => s.is_active)
    if (from === '' && list.length) {
      setFrom((list.find((s) => s.kind === 'WAREHOUSE') ?? list[0]).id)
    }
    if (to === '' && list.length > 1) {
      const wh = list.find((s) => s.kind === 'WAREHOUSE') ?? list[0]
      setTo((list.find((s) => s.kind === 'VAN' && s.id !== wh.id)
             ?? list.find((s) => s.id !== wh.id))!.id)
    }
  }, [stores.data, from, to])

  const filled = lines.filter((l) => l.item)
  const shortfalls = useMemo(() => {
    const need = new Map<number, { it: InvItem; q: number }>()
    for (const l of filled) {
      const q = (Number(l.qty) || 0) * factorOf(l.item!, l.unit)
      const prev = need.get(l.item!.id)
      need.set(l.item!.id, { it: l.item!, q: (prev?.q ?? 0) + q })
    }
    return [...need.values()].filter(({ it, q }) => q > Number(it.stock[String(from)] ?? 0))
      .map(({ it, q }) => t('inv.shortOf', { code: it.code,
        have: fmtQty(it.stock[String(from)] ?? 0), need: fmtQty(q), unit: it.base_unit }))
  }, [filled, from, t])

  const setLine = (key: number, patch: Partial<Line>) => setLines((ls) => {
    const next = ls.map((l) => (l.key === key ? { ...l, ...patch } : l))
    return next[next.length - 1].item ? [...next, blank()] : next
  })
  const cells = useRef<Record<string, HTMLInputElement | null>>({})
  const focusCell = (key: number, col: string) =>
    requestAnimationFrame(() => cells.current[`${key}:${col}`]?.focus())

  const save = useMutation({
    mutationFn: () => createTransfer({
      doc_date: date, from_store_id: Number(from), to_store_id: Number(to),
      notes: notes.trim() || null,
      lines: filled.map((l) => ({ item_code: l.item!.code, qty: l.qty, unit: l.unit })),
    }),
    onSuccess: (x) => {
      void qc.invalidateQueries({ queryKey: ['inv-items'] })
      void qc.invalidateQueries({ queryKey: ['inv-transfers'] })
      setLines([blank()])
      nav(`/inventory/transfers/${x.id}`, { replace: true, state: { saved: true } })
    },
    onError: (e) => setError(errorMessage(e)),
  })
  useUnsavedChanges(filled.length > 0 && !save.isPending && !save.isSuccess)

  const problems = [
    (from === '' || to === '') && t('inv.needStore'),
    from !== '' && from === to && t('stock.sameStore'),
    filled.length === 0 && t('inv.needLine'),
    filled.some((l) => !l.qty || Number(l.qty) <= 0) && t('inv.needQty'),
    shortfalls.length > 0 && t('inv.notEnoughStock'),
  ].filter(Boolean) as string[]
  const ready = problems.length === 0

  useHotkeys('transfer-entry', [
    { combo: 'ctrl+enter', label: t('common.save'), allowInInput: true,
      handler: () => { if (ready && !save.isPending) save.mutate() } },
    { combo: 'alt+a', label: t('inv.addLine'), allowInInput: true,
      handler: () => { const b = blank(); setLines((ls) => [...ls, b]); focusCell(b.key, 'item') } },
  ])

  if (!can(P.inventoryTransfer)) return <Alert severity="warning">{t('report.noPermission')}</Alert>
  const list = stores.data ?? []
  const options = items.data?.items ?? []

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ minWidth: 0 }}>
        <ScreenTitle titleKey="stock.newTransfer" sub={t('stock.newTransferSub')} />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ px: 2.5, pt: 1, pb: 2 }}
          alignItems={{ md: 'flex-start' }}>
          <StoreSelect label={t('stock.from')} value={from} stores={list} onChange={setFrom}
            sx={{ flex: 1, minWidth: 200 }} />
          <Tooltip title={t('stock.swap')}>
            <IconButton aria-label={t('stock.swap')} sx={{ alignSelf: { xs: 'center', md: 'auto' } }}
              onClick={() => { setFrom(to); setTo(from) }}><SwapIcon /></IconButton>
          </Tooltip>
          <StoreSelect label={t('stock.to')} value={to} stores={list} onChange={setTo}
            exclude={from} sx={{ flex: 1, minWidth: 200 }} />
          <Box sx={{ width: { md: 190 } }}>
            <TextField type="date" size="small" label={t('entry.date')} value={date} fullWidth
              InputLabelProps={{ shrink: true }} onChange={(e) => setDate(e.target.value)} />
            <Typography sx={{ fontSize: 11.5, color: c.inkMuted, mt: 0.4, px: 0.25 }}>
              {date ? toHijri(date, i18n.language) : ''}</Typography>
          </Box>
        </Stack>

        {error && <Alert severity="error" sx={{ mx: 2.5, mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Box sx={{ overflowX: 'auto', borderTop: `1px solid ${c.border}` }}>
          <table className="rpt" style={{ minWidth: 640 }}>
            <thead><tr>
              <th style={{ width: 32 }}>#</th>
              <th>{t('inv.item')}</th>
              <th style={{ width: 110 }}>{t('inv.unit')}</th>
              <th className="num" style={{ width: 110 }}>{t('inv.qty')}</th>
              <th className="num" style={{ width: 130 }}>{t('stock.inBase')}</th>
              <th style={{ width: 40 }} />
            </tr></thead>
            <tbody>
              {lines.map((l, idx) => {
                const there = l.item ? Number(l.item.stock[String(from)] ?? 0) : 0
                const base = l.item ? (Number(l.qty) || 0) * factorOf(l.item, l.unit) : 0
                return (
                  <tr key={l.key}>
                    <td className="num" style={{ color: 'var(--ink-muted)' }}>{idx + 1}</td>
                    <td style={{ padding: '4px 8px' }}>
                      <ItemPicker options={options} value={l.item} storeId={from}
                        autoFocus={idx === 0}
                        label={t('inv.itemOnLine', { n: idx + 1 })}
                        inputRef={(el) => { cells.current[`${l.key}:item`] = el }}
                        onPick={(it, unit) => {
                          if (!it) { setLine(l.key, { item: null, unit: '', qty: '' }); return }
                          setLine(l.key, { item: it, unit: unit ?? (it.units.length
                            ? [...it.units].sort((a, b) => Number(b.factor) - Number(a.factor))[0].unit
                            : it.base_unit), qty: '' })
                          focusCell(l.key, 'qty')
                        }} />
                      {l.item && (
                        <Typography sx={{ fontSize: 11.5, px: 0.5, pt: 0.25,
                          color: base > there ? c.dangerInk : c.inkMuted }}>
                          {t('stock.thereNow', { qty: fmtQty(there), unit: l.item.base_unit })}
                        </Typography>
                      )}
                    </td>
                    <td style={{ padding: '4px 8px' }}>
                      <TextField select size="small" fullWidth value={l.item ? l.unit : ''}
                        disabled={!l.item} onChange={(e) => setLine(l.key, { unit: e.target.value })}
                        inputProps={{ 'aria-label': t('inv.unitOnLine', { n: idx + 1 }) }}>
                        {unitsOf(l.item).map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                      </TextField>
                    </td>
                    <td style={{ padding: '4px 8px' }}>
                      <TextField size="small" value={l.qty} disabled={!l.item}
                        inputRef={(el) => { cells.current[`${l.key}:qty`] = el }}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' || e.ctrlKey || e.metaKey) return
                          e.preventDefault()
                          const nextLine = lines[idx + 1]
                          if (nextLine) focusCell(nextLine.key, 'item')
                        }}
                        onChange={(e) => setLine(l.key, { qty: sanitize(e.target.value) })}
                        inputProps={{ inputMode: 'decimal', className: 'num', style: { textAlign: 'end' },
                          'aria-label': t('inv.qtyOnLine', { n: idx + 1 }) }} />
                    </td>
                    <td className="num" style={{ color: 'var(--ink-muted)' }}>
                      {l.item && base ? `${fmtQty(base)} ${l.item.base_unit}` : ''}</td>
                    <td>
                      {l.item && (
                        <IconButton size="small" aria-label={t('inv.removeLine')}
                          onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>
                          <DeleteIcon fontSize="small" /></IconButton>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Box>

        {shortfalls.length > 0 && (
          <Alert severity="error" sx={{ mx: 2.5, mt: 2 }}>
            {t('stock.cannotLoad')} {shortfalls.join(' · ')}</Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ px: 2.5, py: 2 }}
          alignItems={{ sm: 'flex-start' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button size="small" startIcon={<AddIcon />}
              onClick={() => { const b = blank(); setLines((ls) => [...ls, b]); focusCell(b.key, 'item') }}>
              {t('inv.addLine')}</Button>
            <Keycap combo="alt+a" />
          </Stack>
          <TextField size="small" label={t('inv.notes')} value={notes} sx={{ flex: 1 }}
            placeholder={t('stock.notesHint')}
            onChange={(e) => setNotes(e.target.value)} inputProps={{ maxLength: 300 }} />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
        <Box sx={{ flex: '1 1 260px' }}>
          <Typography sx={{ fontSize: 12, color: c.inkMuted }}>{t('inv.lines')}</Typography>
          <Typography className="num" sx={{ fontSize: 22, fontWeight: 600, textAlign: 'start' }}>
            {filled.length}</Typography>
        </Box>
        <Box sx={{ flex: '1 1 300px', maxWidth: 420 }}>
          <Button fullWidth variant="contained" size="large" disabled={!ready || save.isPending}
            onClick={() => save.mutate()}>
            {t('stock.saveTransfer')}
            <Box component="span" sx={{ ml: 1, display: { xs: 'none', sm: 'inline' } }}>
              <Keycap combo="ctrl+enter" /></Box>
          </Button>
          <Typography sx={{ fontSize: 12, mt: 1,
            color: problems.length && filled.length ? c.warningInk : c.inkMuted }}>
            {problems.length && filled.length ? problems[0] : t('stock.transferPosts')}</Typography>
        </Box>
      </Paper>
    </Stack>
  )
}
